import { createTheme } from '@material-ui/core/styles';
import { red } from '@material-ui/core/colors';
import {
  blueSky,
  greenDark,
  white,
  greenBlueAnalytic,
  greyMid,
  greenLight,
  greenLightAnalytic,
  blueDeepSeaAnalytic,
} from '../colors';

export function blurFilter(stdDeviation) {
  return `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' ><filter id='svgMask'><feGaussianBlur stdDeviation='${stdDeviation}' /></filter></svg>#svgMask")`;
}

const primaryMainColor = '#09B4D6';

const skyBlueToCyan = `linear-gradient(270deg, ${blueSky} 0%, ${primaryMainColor} 100%)`;

const theme: any = createTheme({
  typography: {
    fontFamily: [
      'GT Walsheim Pro',
      'Work Sans',
      'Raleway',
      'Calibri',
      'Poppins',
      'Roboto',
      'Google Sans',
      'Arial',
      'sans-serif',
    ].join(','),
    fontFamilySecondary: 'Raleway',
    subtitle1: { fontSize: 12 },
    subtitle2: { fontSize: 17.5 },
    body1: { fontSize: 16 },
    body2: { fontSize: 14 },
    h6: { fontSize: 20 },
    h5: { fontSize: 24 },
    h4: { fontSize: 28 },
    h3: { fontSize: 32 },
    h2: {
      fontSize: 34,
      fontWeight: 700,
    },
  },
  palette: {
    text: { primary: '#3C3744' },
    custom: {
      actions: '#C2CFE0',
    },
    status: {
      complete: '#0d47a1',
      active: '#46BF7E',
      scheduled: '#885AF8',
      draft: '#FFB946',
      rejected: '#EF476F',
    },
    brand: {
      black: '#000000',
      blackOpacityToGrey: 'rgba(0, 0, 0, 0.64)',
      blue: '#0186D1',
      blueDark: '#1778F2',
      blueDeepSeaAnalytic,
      blueGrey: '#C2CFE0',
      blueGreyLight: '#F2F6FB',
      blueish: '#69AAF9',
      blueLight: '#109CF1',
      blueSky: blueSky,
      cream: '#F8F3EE',
      dark: '#3C3744',
      green: '#46BF7E',
      greenBlueAnalytic,
      greenDark: greenDark,
      greenDeep: '#3B7878',
      greenNature: '#688888',
      greenLight: greenLight,
      greenLightAnalytic,
      grey: '#C4C4C4',
      greyMid: greyMid,
      greyMid2: '#F8F8F8',
      greyMid3: '#EBEBEB',
      greyMid4: '#F6F6F6',
      greyFaded: 'rgba(0, 0, 0, 0.6)',
      greyCyan: '#ced4da',
      greyDark: '#737276',
      greyLight: '#F0F3F5',
      greyLight2: '#00000014',
      greyLight3: '#00000008',
      greyWhite: '#CDD7D7',
      mint: '#A3F9E4',
      orange: '#E0A819',
      orangeDark: '#F9713A',
      oceanBlue: '#00809C',
      pink: '#EB507E',
      pinkLight: '#EACFFF',
      purple: '#7A52BA',
      red: '#EF476F',
      yellow: '#EBD66A',
      yellowAlert: '#FFFB8F',
      white: white,
    },
    analyticsColors: [
      '#5A3FFF',
      blueDeepSeaAnalytic,
      '#1ED6FF',
      '#ADE1FF',
      greenLightAnalytic,
      greenBlueAnalytic,
      '#7f2ed9',
      '#b880e1',
    ],
    notification: {
      main: blueSky,
      text: '#3C3744',
    },
    gradient: {
      accountHeader: skyBlueToCyan,
    },
    primary: {
      main: primaryMainColor,
      light: '#F4FEFF',
    },
    default: {
      main: greenDark,
    },
    secondary: {
      main: '#dc004e',
    },
    info: {
      main: '#26BFF1',
    },
    error: {
      main: red[600],
      dark: '#FFA89C',
      light: '#FFF3F1',
    },
    active: {
      light: '#EBFFF8',
      dark: '#92FFDC',
    },
    background: {
      default: white,
      dark: primaryMainColor,
      login: '#F8F3EE',
      banner: '#F8F3EE',
      appBar: 'transparent',
    },
    lightBlack: '#4c4c4c',
    lightGrey: '#EEF1F8',
  },
  backgroundImage: 'background/airplanes.jpg',
  boxShadow: {
    main: '1px 2px 8px 1px rgba(9, 180, 214, 0.4)',
    secondary: '1px 2px 8px 1px rgba(115, 114, 118, 0.2)',
  },
  fontSizes: { onboarding: 17 },
  blur: blurFilter,
  overrides: {
    MuiCircularProgress: {
      colorSecondary: {
        color: greenLight,
      },
    },
    MuiButton: {
      root: {
        textTransform: 'none',
        borderRadius: 8,
      },
      containedPrimary: {
        color: greenDark,
        backgroundColor: blueSky,
        fontWeight: 700,
        transition: 'all 0.3s',
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: primaryMainColor,
          color: 'white',
          boxShadow: 'none',
        },
      },
      containedSecondary: {
        color: greenDark,
        backgroundColor: white,
        fontWeight: 500,
        fontSize: 16,
        lineHeight: 1.5,
        cursor: 'pointer',
        transition: 'all 0.3s',
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: greyMid,
          boxShadow: 'none',
        },
      },
      outlinedSecondary: {
        color: greenDark,
        backgroundColor: white,
        border: `2px solid ${greenDark}`,
        borderRadius: 12,
        fontWeight: 500,
        fontSize: 16,
        lineHeight: 1.25,
        cursor: 'pointer',
        transition: 'all 0.3s',
        boxShadow: 'none',
        paddingRight: 12,
        paddingLeft: 12,
        '&:hover': {
          border: `2px solid ${greenDark}`,
          backgroundColor: greyMid,
          boxShadow: 'none',
        },
        '& .MuiButton-label': {
          lineHeight: 1.25,
          fontSize: 16,
        },
      },
    },
    MuiCheckbox: { colorPrimary: { color: '#2D9CDB' } },
  },
  zIndex: {
    pageContent: 100,
    guidingAlerts: 200,
  },
  props: {
    MuiTypography: {
      variantMapping: { body1: 'h1' },
    },
  },
} as any);

export default theme;
